import { makeAutoObservable } from 'mobx'
import axios from 'axios'
import logger from '../utils/logger'
import apiClient from '../utils/apiClient'
import { config } from '../config'

// Public static translations are available before authentication. They must not
// go through apiClient, whose 401 handler attempts to refresh the session.
const publicI18nClient = axios.create({ baseURL: config.apiUrl })
const RUSSIAN_UI_MIGRATION_KEY = 'reldo:russian-ui-v1'

function resolveInitialLanguage(sessionLanguage) {
	if (sessionLanguage) return sessionLanguage
	const storedLanguage = localStorage.getItem('lang')
	if (!localStorage.getItem(RUSSIAN_UI_MIGRATION_KEY)) {
		localStorage.setItem(RUSSIAN_UI_MIGRATION_KEY, '1')
		if (!storedLanguage || storedLanguage === 'en') {
			localStorage.setItem('lang', 'ru')
			return 'ru'
		}
	}
	return storedLanguage || 'ru'
}

class I18nStore {
	currentLanguage = resolveInitialLanguage() // Однократно переводим старый английский default на русский
	currentCountry = localStorage.getItem('country') || 'RU' // Страна по умолчанию
	translations = {} // Здесь будут храниться загруженные переводы
	availableLanguages = [] // Список доступных языков из базы данных
	availableCountries = [] // Список доступных стран из базы данных

	constructor() {
		makeAutoObservable(this)
	}

	// Устанавливаем текущий язык и загружаем переводы
	async setLanguage(lang) {
		this.currentLanguage = lang
		localStorage.setItem(RUSSIAN_UI_MIGRATION_KEY, '1')
		localStorage.setItem('lang', lang) // Сохраняем язык в localStorage
		
		// Сохраняем выбранный язык на сервере
		try {
			await apiClient.put('/api/user/language', { languageCode: lang })
			logger.log(`Language saved to DB: ${lang}`)
		} catch (error) {
			logger.error('Error saving language to DB:', error)
		}
		
		const translations = await this.fetchTranslations(lang)
		this.translations = translations
	}
	
	// Устанавливаем текущую страну
	async setCountry(country) {
		logger.log('Поменяли страну')
		
		this.currentCountry = country
		localStorage.setItem('country', country) // Сохраняем страну в localStorage
		
		// Сохраняем выбранную страну на сервере
		try {
			await apiClient.put('/api/user/country', { countryCode: country })
			logger.log(`Country saved to DB: ${country}`)
		} catch (error) {
			logger.error('Error saving country to DB:', error)
		}
		
		logger.log(`Country set to: ${country}`)
	}

	// Загружаем языки из базы данных
	async fetchAvailableLanguages() {
		try {
			const response = await apiClient.get('/api/languages')
			this.availableLanguages = response.data
			logger.log('Available languages loaded:', this.availableLanguages)
		} catch (error) {
			logger.error('Error fetching available languages:', error)
			this.availableLanguages = []
		}
	}
	
	// Загружаем страны из базы данных
	async fetchAvailableCountries() {
		try {
			const response = await apiClient.get('/api/countries')
			this.availableCountries = response.data
			logger.log('Available countries loaded:', this.availableCountries)
		} catch (error) {
			logger.error('Error fetching available countries:', error)
			this.availableCountries = []
		}
	}

	// Инициализируем язык и страну из данных сессии или localStorage
	async initializeLanguage(sessionLanguage, { loadCatalogs = true } = {}) {
		logger.log('initializeLanguage....')
		
		// Пытаемся загрузить доступные языки и страны из базы данных,
		// но не блокируем инициализацию, если запросы не удались (401/403)
		if (loadCatalogs) {
			try {
				await Promise.all([
					this.fetchAvailableLanguages(),
					this.fetchAvailableCountries()
				])
			} catch (error) {
				logger.warn('Failed to load languages or countries, continuing with defaults')
			}
		}

		// Новая русская локализация однократно мигрирует старый default en в ru.
		// После миграции явный выбор языка пользователя снова сохраняется как обычно.
		const lang = resolveInitialLanguage(sessionLanguage)
		this.currentLanguage = lang

		// Загружаем переводы
		const translations = await this.fetchTranslations(lang)
		this.translations = translations

		// Сохраняем текущий язык в localStorage
		localStorage.setItem('lang', lang)
	}
	
	// Инициализируем страну из данных сессии
	initializeCountry(sessionCountry) {
		logger.log('initializeCountry....')
		// Используем страну из сессии, если доступна, иначе из localStorage или 'RU' по умолчанию
		const country = sessionCountry || localStorage.getItem('country') || 'RU'
		this.currentCountry = country
		
		// Сохраняем текущую страну в localStorage
		localStorage.setItem('country', country)
	}

	// Выполняем запрос к серверу для получения переводов
	async fetchTranslations(lang) {
		try {
			const response = await publicI18nClient.get(`/locales/${lang}/translation.json`)
			const translations = response.data

			// Fallback на английский, если текущий язык не полный
			if (lang !== 'ru') {
				const fallbackResponse = await publicI18nClient.get('/locales/ru/translation.json')
				const fallbackTranslations = fallbackResponse.data
				return { ...fallbackTranslations, ...translations }
			}

			return translations
		} catch (error) {
			logger.error('Error fetching translations:', error)
			logger.warn('Using hardcoded default translations for login page')
			
			// Возвращаем минимальный набор переводов для страницы логина
			// Это позволит приложению работать без серверных запросов
			return {
				menu: {
						dashboard: 'Главная',
						requests: 'Заявки',
						properties: 'Объекты',
						clients: 'Клиенты',
						colleagues: 'Коллеги',
						settings: 'Настройки',
						help: 'Помощь'
				},
				buttons: {
						save: 'Сохранить',
						cancel: 'Отмена',
						yes: 'Да'
				},
				login: {
						title: 'Вход в систему',
						email: 'Электронная почта',
						password: 'Пароль',
						submit: 'Войти',
						forgotPassword: 'Забыли пароль?',
						noAccount: 'Нет учётной записи?',
						register: 'Зарегистрироваться'
				},
				register: {
						title: 'Регистрация',
						password: 'Пароль',
						confirmPassword: 'Подтвердите пароль',
						submit: 'Зарегистрироваться',
						haveAccount: 'Уже есть учётная запись?',
						passwordMismatch: 'Пароли не совпадают',
						captchaRequired: 'Подтвердите, что вы не робот'
				},
				forgotPassword: {
						title: 'Восстановление пароля',
						submit: 'Отправить письмо',
						backToLogin: 'На страницу входа',
						successMessage: 'Ссылка на сброс пароля отправлена. Проверьте почту.'
				}
			}
		}
	}
}

const i18nStore = new I18nStore()
export default i18nStore
